<template>
  <div class="course-manager">
    <div class="manager-header">
      <h2>Courses</h2>
      <button @click="showCreateModal = true" class="btn btn-primary">
        <i class="fas fa-plus"></i>
        Add Course
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading courses...</p>
    </div>

    <!-- Courses Grid -->
    <div v-else class="courses-grid">
      <div
        v-for="course in courses"
        :key="course.id"
        class="course-card"
      >
        <!-- Course Thumbnail Section -->
        <div class="course-thumbnail-section">
          <div v-if="course.thumbnail && !course._thumbnailFailed" class="thumbnail-preview">
            <img 
              :src="getThumbnailUrl(course.thumbnail)" 
              :alt="course.title"
              @error="handleThumbnailError($event, course)"
              @load="handleThumbnailLoad(course)"
              crossorigin="anonymous"
            />
          </div>
          <div v-else class="thumbnail-placeholder">
            <i class="fas fa-image"></i>
            <span>{{ course.thumbnail ? 'Failed to load' : 'No thumbnail' }}</span>
          </div>
          <button 
            @click.stop="generateThumbnail(course)" 
            class="btn btn-thumbnail-generate"
            :disabled="generatingThumbnailFor === course.id"
            :title="course.thumbnail ? 'Regenerate AI thumbnail' : 'Generate AI thumbnail'"
          >
            <i v-if="generatingThumbnailFor === course.id" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-magic"></i>
            <span v-if="generatingThumbnailFor === course.id">Generating...</span>
            <span v-else>{{ course.thumbnail ? 'Regenerate' : 'Generate' }} AI Thumbnail</span>
          </button>
        </div>

        <div class="course-header">
          <div class="course-badges">
            <span class="status-badge" :class="`status-${course.status?.toLowerCase()}`">
              {{ course.status }}
            </span>
            <span class="type-badge">{{ formatCourseType(course.course_type) }}</span>
          </div>
        </div>

        <h3 class="course-title">{{ course.title }}</h3>
        <p class="course-description">{{ course.short_description || 'No description' }}</p>

        <div class="course-meta">
          <span v-if="course.category_name">
            <i class="fas fa-folder"></i>
            {{ course.category_name }}
          </span>
          <span>
            <i class="fas fa-users"></i>
            {{ course.enrollment_count || 0 }} enrolled
          </span>
          <span v-if="course.is_public" class="public-badge">
            <i class="fas fa-globe"></i>
            Public
          </span>
        </div>

        <div class="course-actions">
          <button @click="editCourse(course)" class="btn btn-small btn-secondary">
            <i class="fas fa-edit"></i>
            Edit
          </button>
          <button @click="deleteCourse(course)" class="btn btn-small btn-danger">
            <i class="fas fa-trash"></i>
            Delete
          </button>
        </div>
      </div>

      <div v-if="courses.length === 0" class="empty-state">
        <i class="fas fa-book-open"></i>
        <h3>No courses yet</h3>
        <p>Create your first course to start teaching</p>
        <button @click="showCreateModal = true" class="btn btn-primary">
          <i class="fas fa-plus"></i>
          Create Course
        </button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || editingCourse" class="modal-overlay">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ editingCourse ? 'Edit Course' : 'Create Course' }}</h2>
          <button @click="closeModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Basic Info -->
          <div class="form-section">
            <h3>Basic Information</h3>
            
            <div class="form-group">
              <label>Title * <span class="char-count">{{ formData.title?.length || 0 }}/300</span></label>
              <input
                v-model="formData.title"
                type="text"
                placeholder="Course title"
                class="form-input"
                maxlength="300"
              />
            </div>

            <div class="form-group">
              <label>Full Description *</label>
              <textarea
                v-model="formData.description"
                placeholder="Detailed course description (required)..."
                class="form-textarea"
                rows="4"
                required
              ></textarea>
              <small>This will be shown on the course detail page</small>
            </div>

            <div class="form-group">
              <label>Course Includes <span class="char-count">{{ formData.short_description?.length || 0 }}/500</span></label>
              <textarea
                v-model="formData.short_description"
                placeholder="Brief description for course cards (optional)...&#10;Example:&#10;✓ Instructional Video&#10;✓ Surgical Demonstrations&#10;✓ Video Navigation Thumbnails"
                class="form-textarea"
                rows="4"
                maxlength="500"
              ></textarea>
              <small>Brief summary shown on course cards (use checkmarks ✓ for list items)</small>
            </div>

            <div class="form-group">
              <label>What You'll Learn <span class="info-label">(Learning Objectives)</span></label>
              <textarea
                v-model="formData.learning_objectives_text"
                placeholder="Enter learning objectives, one per line:&#10;Course Includes:&#10;• Instructional Video - learn directly from experts&#10;• Surgical Demonstrations&#10;• Video Navigation Thumbnails - instantly jump to points of interest"
                class="form-textarea"
                rows="8"
              ></textarea>
              <small>Enter each learning objective on a new line. Start with "Course Includes:" for consistency.</small>
            </div>

            <div class="form-group">
              <label>Tags</label>
              <input
                v-model="formData.tags_text"
                type="text"
                placeholder="compliance, healthcare, training, certification"
                class="form-input"
              />
              <small>Comma-separated tags for search and filtering</small>
            </div>
          </div>

          <!-- Course Details -->
          <div class="form-section">
            <h3>Course Details</h3>
            
            <div class="form-row">
              <div class="form-group">
                <label>Course Type</label>
                <select v-model="formData.course_type" class="form-select">
                  <option value="ON_DEMAND">On-Demand</option>
                  <option value="LIVE_EVENT">Live Event</option>
                  <option value="WEBINAR">Webinar</option>
                  <option value="WORKSHOP">Workshop</option>
                  <option value="CERTIFICATION">Certification</option>
                </select>
              </div>

              <div class="form-group">
                <label>Difficulty</label>
                <select v-model="formData.difficulty_level" class="form-select">
                  <option value="BEGINNER">Beginner</option>
                  <option value="INTERMEDIATE">Intermediate</option>
                  <option value="ADVANCED">Advanced</option>
                  <option value="EXPERT">Expert</option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Status</label>
                <select v-model="formData.status" class="form-select">
                  <option value="DRAFT">Draft</option>
                  <option value="PUBLISHED">Published</option>
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>

              <div class="form-group">
                <label>Medical Field / Animal Class</label>
                <select v-model="formData.category" class="form-select category-select">
                  <option :value="null">No category</option>
                  <optgroup
                    v-for="parentCat in parentCategories"
                    :key="parentCat.id"
                    :label="parentCat.name"
                    class="category-parent"
                  >
                    <option
                      v-for="childCat in getChildCategories(parentCat.id)"
                      :key="childCat.id"
                      :value="childCat.id"
                      class="category-child"
                    >
                      {{ childCat.name }}
                    </option>
                  </optgroup>
                  <!-- Standalone categories without parent -->
                  <option
                    v-for="cat in standaloneCategories"
                    :key="cat.id"
                    :value="cat.id"
                  >
                    {{ cat.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label>Prerequisites</label>
              <textarea
                v-model="formData.prerequisites"
                placeholder="List any prerequisites for this course..."
                class="form-textarea"
                rows="3"
              ></textarea>
              <small>Requirements students should meet before enrolling</small>
            </div>
          </div>

          <!-- Catalog Display & Promotion -->
          <div class="form-section promotion-section">
            <h3>
              <i class="fas fa-star"></i>
              Catalog Display & Promotion
            </h3>
            <p class="section-description">
              Control how this course appears in the catalog to drive enrollment and sales
            </p>
            
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  v-model="formData.is_featured"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span class="checkbox-text">
                  <strong>Featured Course</strong>
                  <small>Display in highlighted featured section at top of catalog</small>
                </span>
              </label>
            </div>

            <div class="form-group">
              <label>
                Sort Priority
                <span class="info-label">(Higher numbers appear first)</span>
              </label>
              <input
                v-model.number="formData.sort_priority"
                type="number"
                placeholder="0"
                class="form-input"
                min="0"
                max="999"
              />
              <small>
                Recommended: 100+ (top priority), 75-99 (high), 50-74 (medium), 0-49 (standard)
              </small>
            </div>
          </div>

          <!-- Target Audience & Public Access -->
          <div class="form-section">
            <h3>Audience & Access</h3>
            
            <div class="form-group">
              <label>Target Audience</label>
              <textarea
                v-model="formData.target_audience"
                placeholder="Who is this course designed for?"
                class="form-textarea"
                rows="3"
              ></textarea>
              <small>Description of the ideal student for this course</small>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input v-model="formData.is_public" type="checkbox" />
                <span>Make course public (visible to all organizations)</span>
              </label>
            </div>
          </div>

          <!-- Duration & Credits -->
          <div class="form-section">
            <h3>Duration & Credits</h3>
            
            <div class="form-row">
              <div class="form-group">
                <label>Estimated Duration (minutes)</label>
                <input
                  v-model.number="formData.estimated_duration_minutes"
                  type="number"
                  placeholder="60"
                  class="form-input"
                  min="0"
                />
                <small>Total course duration in minutes</small>
              </div>

              <div class="form-group">
                <label>Credit Hours</label>
                <input
                  v-model.number="formData.credit_hours"
                  type="number"
                  placeholder="1.5"
                  class="form-input"
                  step="0.1"
                  min="0"
                />
                <small>Continuing education credit hours</small>
              </div>
            </div>

            <div class="form-group">
              <label>Certification Type <span class="char-count">{{ formData.certification_type?.length || 0 }}/100</span></label>
              <input
                v-model="formData.certification_type"
                type="text"
                placeholder="e.g., RACE Certified, CE Credit, CME"
                class="form-input"
                maxlength="100"
              />
              <small>Type of certification or credits offered</small>
            </div>
          </div>

          <!-- Pricing -->
          <div class="form-section">
            <h3>Pricing</h3>
            
            <div class="form-group">
              <label class="checkbox-label">
                <input v-model="formData.is_free" type="checkbox" />
                <span>Free Course</span>
              </label>
            </div>

            <div v-if="!formData.is_free" class="form-group">
              <label>Price (USD)</label>
              <input
                v-model.number="formData.price"
                type="number"
                placeholder="99.00"
                class="form-input"
                step="0.01"
                min="0"
              />
              <small>Course price in US dollars</small>
            </div>
          </div>

          <!-- Instructor Info -->
          <div class="form-section">
            <h3>Instructor Information</h3>
            
            <div class="form-group">
              <label>Instructor Name <span class="char-count">{{ formData.instructor_name?.length || 0 }}/200</span></label>
              <input
                v-model="formData.instructor_name"
                type="text"
                placeholder="Dr. Jane Smith"
                class="form-input"
                maxlength="200"
              />
            </div>

            <div class="form-group">
              <label>Instructor Credentials <span class="char-count">{{ formData.instructor_credentials?.length || 0 }}/300</span></label>
              <input
                v-model="formData.instructor_credentials"
                type="text"
                placeholder="PhD, Board Certified, 15 years experience"
                class="form-input"
                maxlength="300"
              />
              <small>Professional qualifications and certifications</small>
            </div>

            <div class="form-group">
              <label>Instructor Bio <span class="char-count">{{ formData.instructor_bio?.length || 0 }}/3000</span></label>
              <textarea
                v-model="formData.instructor_bio"
                placeholder="Brief biography of the instructor, highlighting relevant experience and expertise..."
                class="form-textarea"
                rows="6"
                maxlength="3000"
              ></textarea>
              <small>Background information about the instructor shown on course detail page</small>
            </div>
          </div>

          <!-- Quiz/Assessment Section -->
          <div class="form-section quiz-section">
            <h3>
              <i class="fas fa-clipboard-check"></i>
              Quiz & Certification
            </h3>
            <p class="section-description">
              Add a quiz for course certification. Students must pass to receive certificate.
            </p>

            <div class="form-row">
              <div class="form-group">
                <label>Passing Score (%)</label>
                <input
                  v-model.number="formData.quiz_passing_score"
                  type="number"
                  placeholder="70"
                  class="form-input"
                  min="0"
                  max="100"
                />
                <small>Percentage required to pass (default: 70%)</small>
              </div>

              <div class="form-group">
                <label>Quiz Questions</label>
                <div class="quiz-count-badge">
                  <i class="fas fa-question-circle"></i>
                  {{ formData.quiz_questions?.length || 0 }} questions
                </div>
              </div>
            </div>

            <!-- Quiz Questions Editor -->
            <div class="quiz-questions-editor">
              <div 
                v-for="(question, qIndex) in formData.quiz_questions" 
                :key="qIndex"
                class="quiz-question-card"
              >
                <div class="question-header">
                  <span class="question-number">Question {{ qIndex + 1 }}</span>
                  <button 
                    @click="removeQuestion(qIndex)" 
                    class="btn-icon-danger"
                    type="button"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>

                <div class="form-group">
                  <label>Question Text</label>
                  <textarea
                    v-model="question.question"
                    placeholder="Enter your question here..."
                    class="form-textarea"
                    rows="2"
                  ></textarea>
                </div>

                <div class="form-group">
                  <label>Answers</label>
                  <div 
                    v-for="(answer, aIndex) in question.answers" 
                    :key="aIndex"
                    class="answer-row"
                  >
                    <span class="answer-letter">{{ String.fromCharCode(65 + aIndex) }}</span>
                    <input
                      v-model="question.answers[aIndex]"
                      type="text"
                      placeholder="Answer option..."
                      class="form-input answer-input"
                    />
                    <label class="radio-label">
                      <input
                        type="radio"
                        :name="`correct-${qIndex}`"
                        :value="aIndex"
                        v-model.number="question.correct"
                      />
                      <span class="radio-text">Correct</span>
                    </label>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label>Explanation (Optional)</label>
                    <textarea
                      v-model="question.explanation"
                      placeholder="Why is this answer correct?"
                      class="form-textarea"
                      rows="2"
                    ></textarea>
                  </div>

                  <div class="form-group">
                    <label>Timestamp (Optional)</label>
                    <input
                      v-model="question.timestamp"
                      type="text"
                      placeholder="00:05:45"
                      class="form-input"
                    />
                    <small>When this topic was covered</small>
                  </div>
                </div>
              </div>

              <!-- Add Question Button -->
              <button 
                @click="addQuestion" 
                class="btn btn-outline btn-block"
                type="button"
              >
                <i class="fas fa-plus"></i>
                Add Question
              </button>

              <div v-if="formData.quiz_questions?.length === 0" class="empty-quiz-state">
                <i class="fas fa-clipboard-list"></i>
                <p>No quiz questions yet. Click "Add Question" to create your first question.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn btn-secondary">
            Cancel
          </button>
          <button
            @click="saveCourse"
            :disabled="!formData.title || !formData.description"
            class="btn btn-primary"
          >
            <i class="fas fa-save"></i>
            {{ editingCourse ? 'Update' : 'Create' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { lmsAPI } from '@/services/lmsAPI.js'
import { useToast } from 'vue-toastification'

export default {
  name: 'CourseManager',
  setup() {
    const toast = useToast()
    const courses = ref([])
    const categories = ref([])
    const loading = ref(true)
    const showCreateModal = ref(false)
    const editingCourse = ref(null)
    const generatingThumbnailFor = ref(null)
    const formData = ref({
      title: '',
      slug: '',
      description: '',
      short_description: '',
      learning_objectives_text: '',
      tags_text: '',
      course_type: 'ON_DEMAND',
      difficulty_level: 'BEGINNER',
      status: 'DRAFT',
      category: null,
      is_public: false,
      is_featured: false,
      sort_priority: 0,
      prerequisites: '',
      target_audience: '',
      estimated_duration_minutes: null,
      credit_hours: null,
      certification_type: '',
      is_free: true,
      price: 0,
      instructor_name: '',
      instructor_credentials: '',
      instructor_bio: '',
      quiz_questions: [],
      quiz_passing_score: 70
    })

    const loadCourses = async () => {
      loading.value = true
      try {
        const response = await lmsAPI.getCourses()
        courses.value = response.data.results || response.data
        
        // Debug: Log thumbnail data
        console.log('=== Courses loaded ===')
        courses.value.forEach(c => {
          console.log(`Course "${c.title}": thumbnail =`, c.thumbnail)
        })
      } catch (err) {
        console.error('Error loading courses:', err)
        toast.error('Failed to load courses')
      } finally {
        loading.value = false
      }
    }

    const loadCategories = async () => {
      try {
        const response = await lmsAPI.getCategories()
        categories.value = response.data
      } catch (err) {
        console.error('Error loading categories:', err)
      }
    }

    const editCourse = async (course) => {
      try {
        // Fetch fresh data from server to ensure we have the latest version
        console.log('Fetching fresh course data for ID:', course.id)
        const response = await lmsAPI.getCourse(course.id)
        const freshCourse = response.data
        
        console.log('Fresh course data:', freshCourse)
        editingCourse.value = freshCourse
        
        // Convert JSON arrays to text for easy editing
        const learningObjectivesText = Array.isArray(freshCourse.learning_objectives) 
          ? freshCourse.learning_objectives.join('\n')
          : ''
        
        const tagsText = Array.isArray(freshCourse.tags)
          ? freshCourse.tags.join(', ')
          : ''
        
        // Extract category ID if it's an object
        const categoryId = typeof freshCourse.category === 'object' && freshCourse.category !== null
          ? freshCourse.category.id
          : freshCourse.category
        
        formData.value = {
          title: freshCourse.title,
          slug: freshCourse.slug,
          description: freshCourse.description || '',
          short_description: freshCourse.short_description || '',
          learning_objectives_text: learningObjectivesText,
          tags_text: tagsText,
          course_type: freshCourse.course_type,
          difficulty_level: freshCourse.difficulty_level,
          status: freshCourse.status,
          category: categoryId,  // Always use ID for dropdown binding
          is_public: freshCourse.is_public,
          is_featured: freshCourse.is_featured || false,
          sort_priority: freshCourse.sort_priority || 0,
          prerequisites: freshCourse.prerequisites || '',
          target_audience: freshCourse.target_audience || '',
          estimated_duration_minutes: freshCourse.estimated_duration_minutes || null,
          credit_hours: freshCourse.credit_hours || null,
          certification_type: freshCourse.certification_type || '',
          is_free: freshCourse.is_free !== undefined ? freshCourse.is_free : true,
          price: freshCourse.price || 0,
          instructor_name: freshCourse.instructor_name || '',
          instructor_credentials: freshCourse.instructor_credentials || '',
          instructor_bio: freshCourse.instructor_bio || '',
          quiz_questions: freshCourse.quiz_questions || [],
          quiz_passing_score: freshCourse.quiz_passing_score || 70
        }
        
        console.log('Loaded category ID for editing:', categoryId)
        
        showCreateModal.value = true
      } catch (err) {
        console.error('Error loading course for editing:', err)
        toast.error('Failed to load course details')
      }
    }

    const deleteCourse = async (course) => {
      if (!confirm(`Are you sure you want to delete "${course.title}"?`)) return

      try {
        await lmsAPI.deleteCourse(course.id)
        toast.success('Course deleted successfully')
        loadCourses()
      } catch (err) {
        console.error('Error deleting course:', err)
        toast.error('Failed to delete course')
      }
    }

    const saveCourse = async () => {
      try {
        console.log('Starting save course...', editingCourse.value ? 'UPDATE' : 'CREATE')
        
        // Note: Let backend auto-generate slug from title if not explicitly provided
        // This allows backend to handle uniqueness checking and counter suffixes
        // Only include slug in payload if user explicitly set it
        
        // Convert text fields to JSON arrays
        const learning_objectives = formData.value.learning_objectives_text
          ? formData.value.learning_objectives_text
              .split('\n')
              .map(line => line.trim())
              .filter(line => line.length > 0)
              .map(line => line.replace(/^[•\-*]\s*/, '')) // Remove bullet points
          : []

        const tags = formData.value.tags_text
          ? formData.value.tags_text
              .split(',')
              .map(tag => tag.trim())
              .filter(tag => tag.length > 0)
          : []

        // Prepare payload with JSON fields
        const payload = {
          title: formData.value.title,
          // Only include slug if explicitly provided by user
          // Let backend auto-generate from title to avoid uniqueness conflicts
          ...(formData.value.slug ? { slug: formData.value.slug } : {}),
          description: formData.value.description,
          short_description: formData.value.short_description,
          learning_objectives: learning_objectives,
          tags: tags,
          course_type: formData.value.course_type,
          difficulty_level: formData.value.difficulty_level,
          status: formData.value.status,
          // Category is always an ID number (extracted when loading course for edit)
          category: formData.value.category,
          is_public: formData.value.is_public,
          is_featured: formData.value.is_featured,
          sort_priority: formData.value.sort_priority || 0,
          prerequisites: formData.value.prerequisites,
          target_audience: formData.value.target_audience,
          estimated_duration_minutes: formData.value.estimated_duration_minutes,
          credit_hours: formData.value.credit_hours,
          certification_type: formData.value.certification_type,
          is_free: formData.value.is_free,
          price: formData.value.is_free ? 0 : formData.value.price,
          instructor_name: formData.value.instructor_name,
          instructor_credentials: formData.value.instructor_credentials,
          instructor_bio: formData.value.instructor_bio,
          quiz_questions: formData.value.quiz_questions || [],
          quiz_passing_score: formData.value.quiz_passing_score || 70
        }

        console.log('Payload:', payload)

        if (editingCourse.value) {
          console.log('Updating course ID:', editingCourse.value.id)
          const response = await lmsAPI.updateCourse(editingCourse.value.id, payload)
          console.log('Update response:', response)
          toast.success('Course updated successfully')
        } else {
          console.log('Creating new course')
          const response = await lmsAPI.createCourse(payload)
          console.log('Create response:', response)
          toast.success('Course created successfully')
        }

        console.log('Closing modal and reloading courses...')
        closeModal()
        await loadCourses()
        console.log('Save complete!')
      } catch (err) {
        console.error('Error saving course:', err)
        console.error('Error response:', err.response)
        console.error('Error data:', err.response?.data)
        console.log('DETAILED ERROR DATA:', JSON.stringify(err.response?.data, null, 2))
        
        // Extract specific error message
        let errorMsg = 'Failed to save course'
        if (err.response?.data) {
          // Check for field-specific errors
          const errors = err.response.data
          if (typeof errors === 'object' && !errors.message && !errors.error) {
            // Field validation errors
            const fieldErrors = []
            for (const [field, msgs] of Object.entries(errors)) {
              const msgArray = Array.isArray(msgs) ? msgs : [msgs]
              fieldErrors.push(`• ${field}: ${msgArray.join(', ')}`)
            }
            if (fieldErrors.length > 0) {
              errorMsg = 'Validation errors:\n' + fieldErrors.join('\n')
            }
          } else {
            errorMsg = errors.message || errors.error || errors.detail || JSON.stringify(errors)
          }
        }
        
        toast.error(errorMsg, { timeout: 10000 })  // Show error longer
        // Also alert for visibility during testing
        alert('Course Save Error:\n\n' + errorMsg)
      }
    }

    const closeModal = () => {
      showCreateModal.value = false
      editingCourse.value = null
      formData.value = {
        title: '',
        slug: '',
        description: '',
        short_description: '',
        learning_objectives_text: '',
        tags_text: '',
        course_type: 'ON_DEMAND',
        difficulty_level: 'BEGINNER',
        status: 'DRAFT',
        category: null,
        is_public: false,
        is_featured: false,
        sort_priority: 0,
        prerequisites: '',
        target_audience: '',
        estimated_duration_minutes: null,
        credit_hours: null,
        certification_type: '',
        is_free: true,
        price: 0,
        instructor_name: '',
        instructor_credentials: '',
        instructor_bio: '',
        quiz_questions: [],
        quiz_passing_score: 70
      }
    }

    // Quiz editing methods
    const addQuestion = () => {
      if (!formData.value.quiz_questions) {
        formData.value.quiz_questions = []
      }
      formData.value.quiz_questions.push({
        question: '',
        answers: ['', '', '', ''],
        correct: 0,
        explanation: '',
        timestamp: ''
      })
    }

    const removeQuestion = (index) => {
      if (confirm('Remove this question?')) {
        formData.value.quiz_questions.splice(index, 1)
      }
    }

    const formatCourseType = (type) => {
      if (!type) return ''
      return type.replace('_', ' ')
    }

    // Get thumbnail URL with proper media path handling
    const getThumbnailUrl = (thumbnail) => {
      if (!thumbnail) return ''
      
      // Debug: log what we're getting
      console.log('getThumbnailUrl input:', thumbnail)
      
      // Already a full URL
      if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
        console.log('Using full URL:', thumbnail)
        return thumbnail
      }
      
      // Get base URL (remove /api suffix if present)
      let baseUrl = process.env.VUE_APP_API_URL || ''
      if (baseUrl.endsWith('/api')) {
        baseUrl = baseUrl.slice(0, -4)
      }
      
      // Path already includes /media/
      if (thumbnail.startsWith('/media/')) {
        const url = `${baseUrl}${thumbnail}`
        console.log('Path includes /media/:', url)
        return url
      }
      
      // Path starts with media/ (no leading slash)
      if (thumbnail.startsWith('media/')) {
        const url = `${baseUrl}/${thumbnail}`
        console.log('Path starts with media/:', url)
        return url
      }
      
      // Relative path - add /media/
      const url = `${baseUrl}/media/${thumbnail}`
      console.log('Adding /media/ prefix:', url)
      return url
    }

    // Handle thumbnail load error
    const handleThumbnailError = (event, course) => {
      console.error('Thumbnail failed to load for course:', course.title)
      console.error('Failed URL:', event.target.src)
      console.error('Original thumbnail value:', course.thumbnail)
      // Mark as failed so we show placeholder
      course._thumbnailFailed = true
    }

    // Handle successful thumbnail load
    const handleThumbnailLoad = (course) => {
      console.log('Thumbnail loaded successfully for:', course.title)
    }

    // Generate AI thumbnail for a course
    const generateThumbnail = async (course) => {
      if (generatingThumbnailFor.value === course.id) return
      
      generatingThumbnailFor.value = course.id
      toast.info(`Generating AI thumbnail for "${course.title}"...`, { timeout: 3000 })
      
      try {
        const response = await lmsAPI.generateCourseThumbnail(course.id)
        
        if (response.data.success) {
          // Update the course in the list with new thumbnail
          const courseIndex = courses.value.findIndex(c => c.id === course.id)
          if (courseIndex !== -1) {
            courses.value[courseIndex].thumbnail = response.data.thumbnail_url
          }
          
          toast.success('AI thumbnail generated successfully!', { timeout: 5000 })
        } else {
          toast.error(response.data.error || 'Failed to generate thumbnail')
        }
      } catch (err) {
        console.error('Error generating thumbnail:', err)
        const errorMsg = err.response?.data?.error || 
                        err.response?.data?.message || 
                        'Failed to generate thumbnail. Please try again.'
        toast.error(errorMsg, { timeout: 8000 })
      } finally {
        generatingThumbnailFor.value = null
      }
    }

    // Computed properties for category hierarchy
    const parentCategories = computed(() => {
      return categories.value.filter(cat => !cat.parent)
    })

    const standaloneCategories = computed(() => {
      return categories.value.filter(cat => !cat.parent && !hasChildren(cat.id))
    })

    const getChildCategories = (parentId) => {
      return categories.value.filter(cat => cat.parent === parentId)
    }

    const hasChildren = (categoryId) => {
      return categories.value.some(cat => cat.parent === categoryId)
    }

    onMounted(async () => {
      await Promise.all([
        loadCourses(),
        loadCategories()
      ])
    })

    return {
      courses,
      categories,
      parentCategories,
      standaloneCategories,
      getChildCategories,
      loading,
      showCreateModal,
      editingCourse,
      formData,
      generatingThumbnailFor,
      editCourse,
      deleteCourse,
      saveCourse,
      closeModal,
      formatCourseType,
      addQuestion,
      removeQuestion,
      getThumbnailUrl,
      generateThumbnail,
      handleThumbnailError,
      handleThumbnailLoad
    }
  }
}
</script>

<style scoped>
.course-manager {
  width: 100%;
}

.manager-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
}

.manager-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--color-text-primary);
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-lg);
}

.course-card {
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  transition: all 0.2s ease;
}

.course-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* Thumbnail Section Styles */
.course-thumbnail-section {
  margin-bottom: var(--spacing-md);
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.thumbnail-preview {
  width: 100%;
  height: 160px;
  overflow: hidden;
}

.thumbnail-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  width: 100%;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0.8;
}

.thumbnail-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.thumbnail-placeholder span {
  font-size: 0.85rem;
}

.btn-thumbnail-generate {
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.btn-thumbnail-generate:hover:not(:disabled) {
  background: white;
  color: #764ba2;
}

.btn-thumbnail-generate:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-thumbnail-generate i {
  font-size: 1rem;
}

.btn-thumbnail-generate .fa-spin {
  animation: spin 1s linear infinite;
}

.course-header {
  margin-bottom: var(--spacing-md);
}

.course-badges {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.status-badge,
.type-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge {
  background: #e3f2fd;
  color: #1976d2;
}

.status-published {
  background: #e8f5e9;
  color: #388e3c;
}

.status-draft {
  background: #fff3e0;
  color: #f57c00;
}

.status-archived {
  background: #f5f5f5;
  color: #757575;
}

.type-badge {
  background: #f3e5f5;
  color: #7b1fa2;
}

.course-title {
  font-size: 1.2rem;
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--color-text-primary);
  line-height: 1.4;
}

.course-description {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  margin: 0 0 var(--spacing-md) 0;
  line-height: 1.6;
}

.course-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.course-meta span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.public-badge {
  color: #4caf50;
  font-weight: 600;
}

.course-actions {
  display: flex;
  gap: var(--spacing-sm);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-border);
}

.form-section {
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
  border-bottom: 1px solid var(--color-border);
}

.form-section:last-child {
  border-bottom: none;
}

.form-section h3 {
  margin: 0 0 var(--spacing-lg) 0;
  font-size: 1.1rem;
  color: var(--color-text-primary);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  font-weight: normal;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

/* Reuse styles from CategoryManager */
.loading-container,
.spinner,
.btn,
.btn-small,
.btn-primary,
.btn-secondary,
.btn-danger,
.empty-state,
.modal-overlay,
.modal-content,
.modal-header,
.modal-close,
.modal-body,
.modal-footer,
.form-group,
.form-input,
.form-textarea,
.form-select,
.form-row {
  /* Inherited from CategoryManager styles */
}

.loading-container {
  text-align: center;
  padding: var(--spacing-xxl);
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-small {
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: 0.9rem;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-secondary);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xxl);
  background: #f8f9fa;
  border-radius: var(--border-radius);
}

.empty-state i {
  font-size: 4rem;
  color: var(--color-text-secondary);
  opacity: 0.5;
  margin-bottom: var(--spacing-lg);
}

.empty-state h3 {
  margin: 0 0 var(--spacing-sm) 0;
}

.empty-state p {
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-lg) 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-content {
  background: white;
  border-radius: var(--border-radius);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  padding: var(--spacing-sm);
}

.modal-close:hover {
  color: var(--color-text-primary);
}

.modal-body {
  padding: var(--spacing-xl);
  overflow-y: auto;
  max-height: 70vh;
}

.modal-body small {
  display: block;
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.875rem;
}

/* Promotion Section Styling */
.promotion-section {
  background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
  padding: var(--spacing-lg);
  border-radius: 12px;
  border: 2px solid #667eea40;
}

.promotion-section h3 {
  color: #667eea;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.promotion-section h3 i {
  color: #fbbf24;
}

.promotion-section .section-description {
  color: #6b7280;
  font-size: 0.95rem;
  margin: -0.5rem 0 1rem 0;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  transition: all 0.2s;
}

.checkbox-label:hover {
  border-color: #667eea;
  background: #f9fafb;
}

.form-checkbox {
  margin-top: 0.25rem;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.checkbox-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.checkbox-text strong {
  color: #1f2937;
  font-size: 1rem;
}

.checkbox-text small {
  color: #6b7280;
  font-size: 0.875rem;
}

/* Quiz Section Styling */
.quiz-section {
  background: #f0fdf4;
  padding: var(--spacing-lg);
  border-radius: 12px;
  border: 2px solid #86efac;
}

.quiz-section h3 {
  color: #16a34a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.quiz-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border-radius: 8px;
  border: 2px solid #86efac;
  color: #16a34a;
  font-weight: 600;
}

.quiz-questions-editor {
  margin-top: var(--spacing-md);
}

.quiz-question-card {
  background: white;
  padding: var(--spacing-lg);
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  margin-bottom: var(--spacing-md);
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 2px solid #e5e7eb;
}

.question-number {
  font-weight: 600;
  color: #16a34a;
  font-size: 1rem;
}

.btn-icon-danger {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-icon-danger:hover {
  background: #fee2e2;
  color: #dc2626;
}

.answer-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.answer-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #16a34a;
  color: white;
  font-weight: bold;
  border-radius: 50%;
  flex-shrink: 0;
}

.answer-input {
  flex: 1;
  margin: 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  white-space: nowrap;
}

.radio-label input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.radio-text {
  font-weight: 600;
  color: #16a34a;
}

.empty-quiz-state {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.empty-quiz-state i {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-md);
}

.form-group {
  margin-bottom: var(--spacing-lg);
}

.form-group label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xs);
  font-weight: 600;
  color: var(--color-text-primary);
}

.char-count {
  font-size: 0.85rem;
  font-weight: 400;
  color: var(--color-text-secondary);
  margin-left: var(--spacing-sm);
}

.info-label {
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--color-text-secondary);
  margin-left: var(--spacing-xs);
  font-style: italic;
}

.char-count.warning {
  color: #f59e0b;
}

.char-count.error {
  color: #ef4444;
  font-weight: 600;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: var(--spacing-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius);
  font-size: 1rem;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

/* Category hierarchy styling */
.category-select optgroup {
  font-weight: 700;
  color: #dc2626;
  font-size: 0.95rem;
  padding: 0.5rem 0;
}

.category-select option.category-child {
  padding-left: 1.5rem;
  font-weight: 400;
  color: #374151;
}

.category-select option {
  padding: 0.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.modal-footer {
  padding: var(--spacing-lg) var(--spacing-xl);
  border-top: 1px solid var(--color-border);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
}

@media (max-width: 768px) {
  .courses-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .course-actions {
    flex-direction: column;
  }

  .course-actions .btn {
    width: 100%;
  }

  .modal-content {
    width: 95%;
    max-width: 95%;
    margin: 1rem;
  }

  .modal-body {
    max-height: 60vh;
    padding: var(--spacing-md);
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer .btn {
    width: 100%;
  }
}
</style>

